import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trophy } from 'lucide-react';
import { fetchLeaderboard, type LeaderboardResult } from '../ton/contract';
import { formatInt } from '../lib/format';

interface ReferralLeaderboardProps {
  /** The caller's own referral key, to highlight their row / show their rank. */
  myKey: number | null;
}

const MEDAL = ['🥇', '🥈', '🥉'];

/**
 * Top referrers leaderboard (same signup attribution as the Telegram bot's
 * /top), with the caller's own row highlighted and their standing shown even
 * when they're outside the top-N. Renders nothing on pure-RPC deployments
 * (no indexer/API) or before data loads.
 */
export function ReferralLeaderboard({ myKey }: ReferralLeaderboardProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<LeaderboardResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchLeaderboard(10, myKey)
      .then((res) => {
        if (alive) setData(res);
      })
      .catch(() => {
        if (alive) setData(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [myKey]);

  // Not configured for this deployment (no API proxy) — hide entirely.
  if (!loading && data === null) return null;

  const items = data?.items ?? [];
  const you = data?.you ?? null;
  const inTop = you != null && items.some((i) => i.key === you.key);

  return (
    <div className="leaderboard-card">
      <div className="card-head">
        <Trophy size={16} aria-hidden="true" /> {t('referrals.leaderboard.title')}
      </div>
      <p className="leaderboard-card__sub">{t('referrals.leaderboard.subtitle')}</p>

      {loading ? (
        <div className="leaderboard-list">
          {[0, 1, 2, 3, 4].map((i) => (
            <span key={i} className="skeleton skeleton--row" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="hint">{t('referrals.leaderboard.empty')}</p>
      ) : (
        <ol className="leaderboard-list">
          {items.map((row, idx) => {
            const mine = myKey != null && row.key === myKey;
            return (
              <li
                key={row.key}
                className={`leaderboard-row${mine ? ' leaderboard-row--you' : ''}`}
              >
                <span className="leaderboard-row__rank">{MEDAL[idx] ?? idx + 1}</span>
                <span className="leaderboard-row__name">
                  {mine ? t('referrals.leaderboard.you') : `#${row.key}`}
                </span>
                <span className="leaderboard-row__count">
                  {t('referrals.leaderboard.signups', { count: row.count })}
                </span>
              </li>
            );
          })}
        </ol>
      )}

      {you != null && !inTop && you.rank > 0 && (
        <p className="leaderboard-card__you">
          {t('referrals.leaderboard.yourRank', {
            rank: formatInt(you.rank),
            count: you.count,
          })}
        </p>
      )}
    </div>
  );
}
